import { useModalAction } from '@/components/modal-views/context';
import {
  useInWishlist,
  useInWishListLocal,
  useToggleWishlist,
} from '@/data/wishlist';
import { useMe } from '@/data/user';
import { Pin } from 'lucide-react';
import { LoaderIcon } from 'react-hot-toast';
import { Button, Tooltip } from '@nextui-org/react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { eventEmitter } from '@/lib/events';
import { trackEvent } from '@/data/utils/utils';

type FavoriteButtonProps = {
  productId: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  iconOnly?: boolean;
};

const sizeMap = {
  xs: {
    button: 'h-6 px-1',
    icon: 'h-3 w-3',
  },
  sm: {
    button: 'h-9 px-3',
    icon: 'h-[15px] w-[15px]',
  },
  md: {
    button: 'h-11 px-4',
    icon: 'h-4 w-4',
  },
  lg: {
    button: 'h-12 px-5',
    icon: 'h-5 w-5',
  },
};

export default function FavoriteButton({
  productId,
  className,
  size = 'md',
  iconOnly = false,
}: FavoriteButtonProps) {
  const { isAuthorized } = useMe();
  const { toggleWishlist, isLoading: adding } = useToggleWishlist(productId, {
    onSuccess: (result) => {
      if (result == 1) {
        eventEmitter.emit('wishlist:added');
      }
    },
  });
  const inWishlist = useInWishListLocal(productId);
  const { t } = useTranslation('common');
  const { openModal } = useModalAction();

  function toggle(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();
    if (!isAuthorized) {
      openModal('REGISTER');
      return;
    }
    toggleWishlist({ product_id: productId });
    trackEvent('toggle_wishlist', {
      product_id: productId,
    });
  }

  // const isLoading = adding;
  // if (isLoading) {
  //   return (
  //     <Tooltip content={t('Loading...')}>
  //       <Button
  //         disabled
  //         variant="bordered"
  //         isIconOnly={iconOnly || size === 'xs'}
  //         className={cn(
  //           'watchButton rounded-md text-xs border-1',
  //           sizeMap[size].button,
  //           className
  //         )}
  //       >
  //         <LoaderIcon className={cn('text-current', sizeMap[size].icon)} />
  //       </Button>
  //     </Tooltip>
  //   );
  // }

  return (
    <Tooltip
      content={
        inWishlist
          ? t('Remove from your watchlist')
          : t('Add to your watchlist')
      }
    >
      <Button
        onClick={toggle}
        variant={inWishlist ? 'solid' : 'bordered'}
        // color={inWishlist ? 'primary' : 'primary'}
        isIconOnly={iconOnly || size === 'xs'}
        disableRipple
        disableAnimation
        disabled={adding}
        className={cn(
          'watchButton rounded-md border-1 text-xs',
          sizeMap[size].button,
          !inWishlist && 'text-brand dark:text-brand',
          (iconOnly || size === 'xs') && '!p-0',
          className,
          inWishlist && 'text-light-500 !bg-brand dark:!bg-brand-dark'
        )}
      >
        <div className="flex h-full w-full items-center justify-center">
          <Pin className={cn('rotate-45 text-current', sizeMap[size].icon, inWishlist && '!rotate-0')} />
        </div>
        {!iconOnly && size !== 'xs' && (
          <span className="text-sm font-medium">
            {inWishlist ? 'Unwatch' : 'Watch'}
          </span>
        )}
      </Button>
    </Tooltip>
  );
}
